import { format as formatDate } from 'date-fns';
import { cloneElement, ReactElement } from 'react';
import useFindSuppliersQuery from '@/api/queries/useFindSuppliersQuery';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { SearchProps } from '@/components/search/useSearch';
import StatusActiveIndicator from '@/components/StatusActiveIndicator';
import { isStatusActive } from '@/models/Status';
import { SupplierEditPath } from '@/modules/admin/paths';
import { Link } from 'react-router-dom';

interface Supplier {
  id: string;
  name: string;
  organizationId: string;
}

interface SupplierTableProps extends SearchProps {
  onSelectSupplier?: (supplier: Supplier) => void;
  onSelectSupplierElement?: ReactElement;
  onSelectSupplierLabel?: string;
}

const SupplierTable = ({
  searchQuery,
  page = 1,
  onChangePage = () => {},
  onSelectSupplier = () => {},
  onSelectSupplierElement,
  onSelectSupplierLabel = 'Select supplier',
}: SupplierTableProps) => {
  const { suppliers, isLoading, itemsPerPage } = useFindSuppliersQuery(page, searchQuery);

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Created At</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.items.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={4} className="text-center py-6 text-gray-500">
                  No suppliers found
                </td>
              </tr>
            ) : (
              suppliers.items.map((supplier) => {
                if (!supplier.id) return null;

                return (
                  <tr key={supplier.id}>
                    <td>
                      <Link
                        to={SupplierEditPath.withId(supplier.id)}
                        className="text-blue-500 hover:underline"
                      >
                        {supplier.name}
                      </Link>
                    </td>
                    <td>{supplier.createdAt && formatDate(supplier.createdAt, 'd MMM yyyy')}</td>
                    <td>
                      <StatusActiveIndicator isActive={isStatusActive(supplier)} />
                    </td>
                    <td>
                      {onSelectSupplierElement ? (
                        cloneElement(onSelectSupplierElement, {
                          onClick: () => onSelectSupplier(supplier),
                        })
                      ) : (
                        <Button
                          onClick={() => onSelectSupplier(supplier)}
                          variant="secondary"
                          size="xs"
                        >
                          {onSelectSupplierLabel}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {isLoading && (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        )}
      </div>

      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={suppliers.totalCount || 0}
          estimatedTotalCount={suppliers.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default SupplierTable;
